import React from 'react';
import { Box, Divider, Drawer, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { alpha } from '@mui/material/styles';

import { useConfig } from '@occc/config';
import { Drive_Insights_Variant } from '@occc/graphql';
import { driveChat } from '@occc/feat-feature-flag-control';
import { useFlagEnabled } from '@occc/data-flags';

import { RetailerNetworkSidebar } from '@occc/feat-retailer-connect';
import { DriveInsightsSidebar } from '@occc/feat-drive';
import {
  CommandCenterNavIcon,
  RocketIcon,
  TransactIcon,
  TouchpointIcon,
} from '@occc/ui-design-system/icons';
import {
  buildActivateNavigationItems,
  buildTobaccoNavigationItems,
  dashboardNavigationItem,
  siteManagementNavigationItem,
  transactNavigationItems,
  touchpointNavigationItems,
} from './constants';
import { NestedNav } from './NestedNav';
import { ParIntelligenceSidebarCta } from './ParIntelligenceSidebarCta';
import { TopNav } from './TopNav';

export const DRAWER_WIDTH = 260;

type SidebarProps = {
  onClose: React.MouseEventHandler<HTMLButtonElement>;
  isOpen: boolean;
  isPoweredByAppearance?: boolean;
};

type SidebarHeaderProps = Pick<SidebarProps, 'isOpen' | 'onClose'>;

const DefaultSidebarHeader = ({ isOpen, onClose }: SidebarHeaderProps) => (
  <>
    <Box p={2} display='flex' alignItems='center' justifyContent='space-between'>
      <CommandCenterNavIcon />
      {isOpen && (
        <IconButton onClick={onClose}>
          <ChevronLeftIcon />
        </IconButton>
      )}
    </Box>
    <Divider />
  </>
);

const PoweredBySidebarHeader = ({ isOpen, onClose }: SidebarHeaderProps) => (
  <>
    <Box
      sx={{
        px: 0,
        pt: 0,
        pb: 0,
        background: (theme) =>
          `linear-gradient(180deg, ${theme.palette.common.white} 0%, ${alpha(
            theme.palette.primary.main,
            0.02,
          )} 34%, ${alpha(theme.palette.primary.main, 0.05)} 56%, ${alpha(
            theme.palette.primary.main,
            0.12,
          )} 78%, ${alpha(theme.palette.primary.main, 0.18)} 100%)`,
        borderBottom: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: -1,
          height: '1px',
          background: (theme) =>
            `linear-gradient(90deg, rgba(255,255,255,0) 0%, ${alpha(
              theme.palette.primary.main,
              0.22,
            )} 50%, rgba(255,255,255,0) 100%)`,
        },
      }}
    >
      <Box px={2} pt={2.2} display='flex' alignItems='center' justifyContent='space-between'>
        <CommandCenterNavIcon />
        {isOpen && (
          <IconButton onClick={onClose}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>
      <Typography
        sx={{
          px: 2,
          pt: '12px',
          pb: 1.25,
          color: (theme) => theme.palette.drive.textPrimary,
          fontSize: '1.12rem',
          fontWeight: 700,
          letterSpacing: '0.003em',
          lineHeight: 1.1,
        }}
      >
        <Box component='span' sx={{ fontWeight: 400 }}>
          Powered by
        </Box>{' '}
        PAR Intelligence
        <Box
          component='span'
          sx={{
            fontSize: '0.62em',
            fontWeight: 600,
            verticalAlign: 'super',
            ml: 0.2,
            lineHeight: 1,
            display: 'inline-block',
          }}
        >
          ®
        </Box>
      </Typography>
    </Box>
    <Divider sx={{ opacity: 0 }} />
  </>
);

export const Sidebar = ({ onClose, isOpen }: SidebarProps) => {
  const { version: segmentsVersion } = useConfig('features.segments');
  const isScanDataEnabled = useConfig('scanData.enabled');
  const isExternalTobaccoApiOfferEnabled = useConfig('externalTobaccoApiOffer.enabled');
  const isTouchpointEnabled = useConfig('touchpoint.enabled');
  const isTriggeredMessagesEnabled = useConfig('triggeredMessages.enabled');
  const driveInsightsConfig = useConfig('driveInsights');
  const isDriveChatEnabled = useFlagEnabled(driveChat);
  // Access can be granted tenant-wide (`enabled`) or per-user via Custom Data (the driveChat flag).
  const hasInsightsAccess = Boolean(driveInsightsConfig?.enabled) || isDriveChatEnabled;
  // Once the user has access, the config `variant` decides which experience to surface,
  // independently of `enabled`.
  const driveInsightsVariantConfig = hasInsightsAccess ? driveInsightsConfig?.config : undefined;
  const isDriveInsightsV2 = driveInsightsVariantConfig?.variant === Drive_Insights_Variant.V2;
  const parIntelligenceUrl = isDriveInsightsV2 ? driveInsightsVariantConfig.url : undefined;
  const activateNavigationItems = buildActivateNavigationItems(
    segmentsVersion,
    isTriggeredMessagesEnabled,
  );
  // V2 surfaces the external PAR Intelligence CTA; V1 (default) surfaces the in-app InsightChat.
  const isInsightsExperienceEnabledV2 = isDriveInsightsV2 && Boolean(parIntelligenceUrl);
  const isInsightsExperienceEnabledV1 = hasInsightsAccess && !isInsightsExperienceEnabledV2;
  const tobaccoNavigationItems = buildTobaccoNavigationItems({
    isScanDataEnabled,
    isExternalTobaccoApiOfferEnabled,
  });

  return (
    <Drawer
      variant='persistent'
      open={isOpen}
      anchor='left'
      PaperProps={{ elevation: 2, sx: { width: DRAWER_WIDTH } }}
    >
      {hasInsightsAccess ? (
        <PoweredBySidebarHeader isOpen={isOpen} onClose={onClose} />
      ) : (
        <DefaultSidebarHeader isOpen={isOpen} onClose={onClose} />
      )}
      <Box px={2}>
        {isInsightsExperienceEnabledV2 && parIntelligenceUrl ? (
          <ParIntelligenceSidebarCta url={parIntelligenceUrl} />
        ) : null}
        <TopNav items={[dashboardNavigationItem]} />
        <NestedNav title='Activate' icon={<RocketIcon />} items={activateNavigationItems} />

        <RetailerNetworkSidebar />

        {isTouchpointEnabled && (
          <NestedNav
            title='Touchpoint'
            icon={<TouchpointIcon />}
            items={touchpointNavigationItems}
          />
        )}

        <NestedNav title='Transact' icon={<TransactIcon />} items={transactNavigationItems} />

        <TopNav items={[siteManagementNavigationItem]} />

        {isInsightsExperienceEnabledV1 ? <DriveInsightsSidebar /> : null}

        {(isScanDataEnabled || isExternalTobaccoApiOfferEnabled) && (
          // The icon will be replaced with a scan data icon when available.
          <NestedNav title='Tobacco' icon={<RocketIcon />} items={tobaccoNavigationItems} />
        )}
      </Box>
    </Drawer>
  );
};
